import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, LogOut, Lock, Sparkles, Flame, BookOpen, Trophy, Star, AlertCircle, Crown } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePublicConfig } from "../lib/publicConfig";
import DashboardLayout from "../components/DashboardLayout";
import AssessmentCard from "../components/AssessmentCard";
import ProgressRing from "../components/ProgressRing";
import DashboardCourseCard from "../components/DashboardCourseCard";
import { PromoBanner, SponsorBanner } from "../components/PublicBanners";
import OnboardingTour, { ReplayTourButton } from "../components/OnboardingTour";
import UpcomingEvents from "../components/UpcomingEvents";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";

// Phase 13.7 — Light theme tones for the membership pill on the light surface.
const MEMBERSHIP_LABEL = {
  none: { label: "FREE · NOT ACTIVE", tone: "text-gray-500 border-gray-300 bg-white" },
  staff: { label: "STAFF MEMBER", tone: "text-[#3B82F6] border-[#3B82F6]/30 bg-[#EFF6FF]" },
  executive: { label: "EXECUTIVE MEMBER", tone: "text-fc-red border-fc-red/30 bg-[#FFF1F1]" },
};

export default function Dashboard() {
  const { user, logout, setUser } = useAuth();
  const cfg = usePublicConfig();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [notif, setNotif] = useState({ notifications: [], unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setError("");
      const [d, w, n] = await Promise.all([
        api.get("/dashboard/me").then(r => r.data),
        api.get("/dashboard/widgets").then(r => r.data).catch(() => null),
        api.get("/notifications/me").then(r => r.data).catch(() => ({ notifications: [], unread_count: 0 })),
      ]);
      setData(d);
      setWidgets(w);
      setNotif(n);
      setUser(d.user);
    } catch (e) {
      console.error("Dashboard load failed", e);
      setError("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read", { mark_all: true });
      setNotif((n) => ({ notifications: n.notifications.map(x => ({ ...x, read: true })), unread_count: 0 }));
    } catch {}
  };

  const onAssessmentVerified = () => load();

  if (loading) {
    return (
      <DashboardLayout testid="dashboard-page">
        <div className="flex items-center justify-center h-screen text-gray-500">Loading…</div>
      </DashboardLayout>
    );
  }

  const mem = MEMBERSHIP_LABEL[user?.membership_type] || MEMBERSHIP_LABEL.none;
  const showUpsell = user?.membership_type === "staff" && (data?.locked_courses || []).length > 0;
  const noPlan = user?.membership_type === "none";

  return (
    <DashboardLayout testid="dashboard-page">
      <OnboardingTour />
      <PromoBanner location="dashboard" />
      <SponsorBanner location="dashboard" />

      {/* Top bar — light theme */}
      <div className="h-20 px-6 lg:px-10 flex items-center justify-between border-b border-gray-200 bg-white/90 backdrop-blur sticky top-0 z-30">
        <div data-testid="welcome-header">
          <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500">WELCOME BACK</p>
          <h1 className="text-lg sm:text-xl font-black tracking-tight text-gray-900" data-testid="dashboard-welcome">
            {user?.name?.split(" ")[0]?.toUpperCase()}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <span className={`hidden sm:inline-block text-[10px] tracking-[0.18em] uppercase font-bold border px-3 py-1.5 ${mem.tone}`} data-testid="membership-badge">
            {mem.label}
          </span>
          <span className="text-[10px] tracking-[0.18em] uppercase font-bold border border-fc-red/30 text-fc-red bg-[#FFF1F1] px-3 py-1.5 hidden md:inline-block" data-testid="rank-badge">
            <Crown size={10} className="inline mr-1" /> {user?.current_rank || "Rookie"}
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button className="relative text-gray-500 hover:text-gray-900" data-testid="notification-bell">
                <Bell size={18} />
                {notif.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 text-[9px] font-bold flex items-center justify-center bg-fc-red text-white rounded-full">{notif.unread_count}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 bg-white border-gray-200 text-gray-900 p-0 shadow-lg">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-gray-700">Notifications</p>
                {notif.unread_count > 0 && (
                  <button onClick={markAllRead} className="text-[10px] tracking-[0.18em] uppercase font-bold text-fc-red hover:underline" data-testid="mark-all-read">Mark read</button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notif.notifications.length === 0 ? (
                  <p className="px-4 py-6 text-gray-500 text-sm text-center">All caught up.</p>
                ) : notif.notifications.slice(0, 12).map((n) => (
                  <div key={n.id} className={`px-4 py-3 border-b border-gray-100 ${n.read ? "opacity-60" : ""}`} data-testid="notif-row">
                    <p className="text-sm font-bold text-gray-900">{n.title}</p>
                    <p className="text-[10px] tracking-wider text-gray-500 uppercase mt-1">{n.type}</p>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="text-gray-500 hover:text-gray-900 text-xs font-bold tracking-wider flex items-center gap-1.5"
            data-testid="dashboard-logout"
          >
            <LogOut size={14} /> LOGOUT
          </button>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-8 space-y-10">
        {error && (
          <div className="bg-[#FFF1F1] border border-fc-red/40 text-fc-red rounded-xl p-3 flex items-center gap-2 text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Freddy Intel Assessment — Phase 10 (automotive-only) / Phase 18 kill-switch. */}
        {cfg.freddy_intel_enabled && user?.audience === "automotive" && (
          data?.assessment_pending ? (
            <AssessmentCard user={user} onVerified={onAssessmentVerified} />
          ) : (
            <AssessmentCard user={user} />
          )
        )}

        {/* No-plan banner */}
        {noPlan && (
          <div className="bg-white border border-fc-red/30 rounded-2xl shadow-sm p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between" data-testid="no-plan-banner">
            <div className="max-w-2xl">
              <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-fc-red mb-2">CHOOSE A PLAN</p>
              <h3 className="text-xl sm:text-2xl font-black tracking-tight mb-2 text-gray-900">Unlock the full Freddy library.</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                You&apos;re seeing the free preview of lesson 1. Pick a membership to keep going.
              </p>
            </div>
            <button onClick={() => navigate("/pricing")} className="btn-fc-red whitespace-nowrap" data-testid="no-plan-cta">
              See Plans
            </button>
          </div>
        )}

        {/* PHASE 13 — Your Next Live Events */}
        <section data-testid="dashboard-live-events">
          <div className="flex items-end justify-between mb-5 gap-4">
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 mb-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                YOUR NEXT LIVE EVENTS
              </p>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight text-gray-900">Live operator sessions</h2>
              <p className="text-sm text-gray-500 mt-1">Reserve your spot before they go live.</p>
            </div>
            <ReplayTourButton />
          </div>
          <UpcomingEvents audience={user?.audience} limit={6} authed={true} variant="dashboard" />
        </section>

        {/* Top section: progress ring + KPI bento — light cards */}
        <section className="grid lg:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 flex flex-col items-center justify-center">
            <ProgressRing percent={data?.overall_progress_percent || 0} label="Overall Progress" />
            <p className="text-xs text-gray-500 mt-4 text-center max-w-[180px]">
              Across all your enrolled courses
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Star} label="Total XP" value={data?.kpis?.total_xp ?? 0} tone="text-fc-red" testid="kpi-xp" />
            <Kpi icon={BookOpen} label="Lessons Done" value={data?.kpis?.lessons_completed ?? 0} tone="text-[#3B82F6]" testid="kpi-lessons" />
            <Kpi icon={Flame} label="Day Streak" value={data?.kpis?.current_streak_days ?? 0} tone="text-[#F59E0B]" testid="kpi-streak" />
            <Kpi icon={Trophy} label="Courses Done" value={data?.kpis?.courses_completed ?? 0} tone="text-[#10B981]" testid="kpi-courses" />
          </div>
        </section>

        {/* Phase 3 widgets: Top 3 + Next badge — light theme */}
        {/* Phase 17 — Climb-the-board widget is gated behind leaderboards_enabled. */}
        {widgets && (
          <section className={`grid gap-6 ${cfg.leaderboards_enabled ? "lg:grid-cols-2" : "lg:grid-cols-1"}`} data-testid="widgets-row">
            {cfg.leaderboards_enabled && (
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6" data-testid="top3-week-widget">
              <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500 mb-3" data-testid="top3-period-label">
                {widgets.period === "all_time" ? "TOP 3 · ALL TIME" : "TOP 3 · THIS WEEK"}
              </p>
              <h3 className="font-black text-lg mb-5 text-gray-900">Climb the board.</h3>
              {(widgets.top3_week || []).length === 0 ? (
                <p className="text-gray-500 text-sm">No leaderboard activity yet. Be first.</p>
              ) : (
                <div>
                  {widgets.period === "all_time" && (
                    <p className="text-[11px] text-gray-500 mb-3 -mt-2">No activity this week — showing all-time leaders.</p>
                  )}
                  <ul className="space-y-3">
                  {widgets.top3_week.map((r) => (
                    <li key={r.position} className={`flex items-center justify-between border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 ${r.is_me ? "text-fc-red" : "text-gray-900"}`}>
                      <span className="flex items-center gap-3">
                        <Trophy size={14} className={r.position === 1 ? "text-[#F59E0B]" : r.position === 2 ? "text-[#9CA3AF]" : "text-[#B97D2A]"} />
                        <span className="font-mono font-bold text-sm">{r.alias}</span>
                        {r.is_me && <span className="text-[10px] tracking-[0.18em] uppercase">YOU</span>}
                      </span>
                      <span className="font-black text-base">{r.xp.toLocaleString()} XP</span>
                    </li>
                  ))}
                </ul>
                </div>
              )}
              <button onClick={() => navigate(`/leaderboard?board=${widgets.board || "dealers"}`)} className="mt-4 text-xs tracking-[0.18em] uppercase font-bold text-fc-red hover:underline">View leaderboard →</button>
            </div>
            )}

            {widgets.next_badge && (
              <div className="bg-white border border-fc-red/30 rounded-2xl shadow-sm p-6" data-testid="next-badge-widget">
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-fc-red mb-3">NEXT BADGE TO EARN</p>
                <div className="flex items-start gap-4">
                  {(() => {
                    const Icon = LucideIcons[widgets.next_badge.icon] || LucideIcons.Award;
                    return <div className="w-14 h-14 flex items-center justify-center bg-[#FFF1F1] text-fc-red flex-shrink-0 rounded-xl"><Icon size={28} /></div>;
                  })()}
                  <div>
                    <h3 className="font-black text-lg text-gray-900">{widgets.next_badge.name}</h3>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">{widgets.next_badge.description}</p>
                  </div>
                </div>
                <button onClick={() => navigate("/achievements")} className="mt-4 text-xs tracking-[0.18em] uppercase font-bold text-fc-red hover:underline">See all badges →</button>
              </div>
            )}
          </section>
        )}

        {/* My Courses */}
        <section data-testid="my-courses-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">My Courses</h2>
          </div>
          {(data?.my_courses || []).length === 0 ? (
            <p className="text-gray-500 text-sm">You haven&apos;t started any courses yet. Pick one below.</p>
          ) : (
            <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
              {data.my_courses.map((c) => <DashboardCourseCard key={c.id} course={c} />)}
            </div>
          )}
        </section>

        {/* Recommended */}
        {(data?.recommended_courses || []).length > 0 && (
          <section data-testid="recommended-courses-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">
                Recommended for <span className="text-fc-red">you</span>
              </h2>
            </div>
            <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
              {data.recommended_courses.map((c) => <DashboardCourseCard key={c.id} course={c} ctaLabel="Start" />)}
            </div>
          </section>
        )}

        {/* Locked Executive Upsell */}
        {showUpsell && (
          <section data-testid="locked-upsell-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-fc-red mb-2">EXECUTIVE TRACK</p>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">
                  Upgrade to unlock the <span className="text-fc-red">Executive Mindset</span> track
                </h2>
              </div>
              <button onClick={() => navigate("/pricing")} className="btn-fc-red hidden sm:inline-flex">
                <Lock size={14} /> Upgrade
              </button>
            </div>
            <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
              {data.locked_courses.map((c) => <DashboardCourseCard key={c.id} course={c} locked />)}
            </div>
          </section>
        )}
      </div>

      {/* Mobile bottom-tab nav — keep dark for visibility */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 bg-[#060A24] border-t border-white/10 grid grid-cols-4 z-40" data-testid="mobile-bottom-tabs">
        {[
          { to: "/dashboard", label: "Home", icon: Sparkles },
          { to: "/courses", label: "Courses", icon: BookOpen },
          { to: "/dashboard", label: "Progress", icon: Trophy },
          { to: "/dashboard", label: "More", icon: Star },
        ].map((it, i) => {
          const Icon = it.icon;
          return (
            <button key={i} onClick={() => navigate(it.to)} className="py-3 flex flex-col items-center text-white/65 text-[10px] tracking-wider">
              <Icon size={18} />
              <span className="mt-1">{it.label}</span>
            </button>
          );
        })}
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={testid}>
      <Icon className={`mb-3 ${tone || "text-gray-700"}`} size={20} />
      <p className="text-3xl font-black tracking-tight text-gray-900">{value}</p>
      <p className="text-[10px] tracking-[0.22em] uppercase text-gray-500 mt-1">{label}</p>
    </div>
  );
}
